import { AUTOPX_DISCOUNT_ID } from 'data-layer-cr/dist/constants/discounts';
import {
  Discount,
  Quotation,
  QuotationUnit,
  ServerResponse,
} from 'data-layer-cr/dist/models';

// Data layer
import { COMPLETE_VEHICLE } from 'data-layer-cr/dist/constants/products';
import { isSpecialPlan } from 'data-layer-cr/dist/functions/quotations';
import QuotationProfile from 'data-layer-cr/dist/models/QuotationProfile';

// models
import PricingPreviewRequest from 'src/models/requestTypes/PricingPreviewRequest';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';
import { PricingV4Quotation } from 'src/models/responseTypes/PricingV4Response';
import { makeQuotationFromPricingV4 } from 'src/models/responseTypes/functions';

// Network
import processRequest from 'src/services/processRequest';

import CarItemFromLicense from 'src/models/Quotation/CarItemFromLicense';
import {
  BROKER_LICENSE,
  CAR_BRAND,
  CAR_MODEL,
  CAR_STATE,
  CAR_VALUE,
  CAR_YEAR,
  CLIENT_BIRTHDATE,
  CLIENT_GENDER,
  CLIENT_NAME,
  COVERAGE_TYPE,
  DEDUCTIBLE_OPTION,
  ID_QUOTATION,
  PACKAGE_RC,
  PREMIUM,
  QUOTATION_CREATED_STEP,
  QUOTATION_DATE,
  SELECTED_ENDORSMENT,
  trackEvent,
} from '../../../analytics';

// Methods
import {
  getCarState,
  getSelectedEndorsment,
} from '../../../functions/quotationMethods';

// Request/Response types
import getPackageDescription from '../../../functions/getPackageDescription';

/** Helpers */
export const QUOTATION_CREATION_ERROR = 'La cotización no pudo ser creada.';
export function makeQuotationCreationError(): string {
  return QUOTATION_CREATION_ERROR;
}
export const QUOTATION_CLONE_ERROR = 'Error al intentar editar cotización...';
export function makeQuotationCloneError(): string {
  return QUOTATION_CLONE_ERROR;
}

/**
 * This function checks if the quotation includes a profile or a license that has autoPX
 * this will only apply to ASE since AGE always has auto PX
 * @param isAutoPx boolean determined by the broker license endpoint
 * @param quotationProfile given by the pricing endpoint
 * @param loggedRole given by the function getLoggedRole
 * @returns boolean
 */
export const appliesAutoPxSpecialPlans = (
  isLicenseAutoPx: boolean,
  quotationProfile: QuotationProfile,
  loggedRole: string,
): boolean => {
  if (loggedRole === 'ASE') {
    if (isLicenseAutoPx || isSpecialPlan(quotationProfile)) {
      return true;
    }
    return false;
  }
  return true;
};

/** Temporary, should be removed by sprint 68 */
export const appliesNewAutoPxDiscount = (
  isAutoPx: boolean,
): boolean | undefined => isAutoPx;

export const findAutoPxIndex = (quotationDiscounts: Discount[]): number =>
  quotationDiscounts.findIndex(
    (discount) => discount.id === AUTOPX_DISCOUNT_ID,
  );

export const findAutoPxUnit = (
  quotationDiscounts: Discount[] | null,
): Discount | undefined => {
  if (quotationDiscounts) {
    return quotationDiscounts.find(
      (discount) => discount.id === AUTOPX_DISCOUNT_ID,
    );
  }
  return undefined;
};

export const updateQuotationUnits = (
  units: QuotationUnit[],
  responseUnits: QuotationUnit[],
): QuotationUnit[] => {
  const updatedUnits: QuotationUnit[] = [];

  units.forEach((unit, index) => {
    const responseAutoPxTotal = findAutoPxUnit(
      responseUnits[index].total.discounts,
    );

    if (responseAutoPxTotal && unit.total.discounts) {
      const autoPxIndex = findAutoPxIndex(unit.total.discounts);

      // eslint-disable-next-line no-param-reassign
      unit.total.discounts[autoPxIndex].total = responseAutoPxTotal.total;
      updatedUnits.push(unit);
    }
  });

  if (updatedUnits.length === 0) {
    return units;
  }
  return updatedUnits;
};

/** End of Temporary functions */

export const COMPLETE = 'Complete_Vehicle';
export const RC_VEHICLE = 'RC_Vehicle';

export const getPackageDescriptionOption = (
  packageType: string,
  productId: string,
  selectedDeductibleOption: string,
): string => {
  if (packageType === COMPLETE && productId === COMPLETE_VEHICLE) {
    return selectedDeductibleOption;
  }

  if (packageType === RC_VEHICLE && productId !== COMPLETE_VEHICLE) {
    return selectedDeductibleOption;
  }

  return '';
};

/**
 * Helper function to track created quotation into mixpanel.
 *
 * @param currentQuotation
 * @param brands
 * @param models
 */
export function trackCreatedQuotationEvent(
  currentQuotation: Quotation,
  deductibleOption: number,
): void {
  const selectedDeductibleOption = getPackageDescription(
    currentQuotation,
    deductibleOption,
  );

  const packageDescriptionComplete = getPackageDescriptionOption(
    COMPLETE,
    currentQuotation.productId,
    selectedDeductibleOption,
  );

  const packageDescriptionRC = getPackageDescriptionOption(
    RC_VEHICLE,
    currentQuotation.productId,
    selectedDeductibleOption,
  );

  trackEvent({
    eventName: QUOTATION_CREATED_STEP,
    eventProperties: {
      [BROKER_LICENSE]: currentQuotation.brokerLicense,
      [CLIENT_NAME]: currentQuotation.summary.customerName,
      [CLIENT_GENDER]: currentQuotation.customer.gender,
      [CLIENT_BIRTHDATE]: currentQuotation.customer.birthday,
      [CAR_BRAND]: currentQuotation.summary.carBrand,
      [CAR_MODEL]: currentQuotation.summary.carModel,
      [CAR_YEAR]: currentQuotation.summary.carYear,
      [CAR_VALUE]: currentQuotation.summary.insuranceValue.toString(),
      [COVERAGE_TYPE]: currentQuotation.productId,
      [PREMIUM]: currentQuotation.summary.anualPayment.toString(),
      [QUOTATION_DATE]: currentQuotation.createdAt,
      [CAR_STATE]: getCarState(currentQuotation.units[0].unit.isNewCar),
      [DEDUCTIBLE_OPTION]: packageDescriptionComplete,
      [SELECTED_ENDORSMENT]: getSelectedEndorsment(
        currentQuotation.units[0].coverages,
      ),
      [ID_QUOTATION]: currentQuotation.id.toString(),
      [PACKAGE_RC]: packageDescriptionRC,
    },
  });
}

export const haveQuotationProfile = (
  userProfiles: PricingProfileResponse[],
  createdProfile: QuotationProfile,
): boolean => {
  const matchProfile = userProfiles.find(
    (quotationProfile) => quotationProfile.id === createdProfile.id,
  );

  return matchProfile !== undefined || !isSpecialPlan(createdProfile);
};

export const requestQuotationPricingByProfile = async (
  payload: PricingPreviewRequest,
  currentProfile: PricingProfileResponse,
): Promise<Quotation> => {
  const UNIQUE_PRICING_REQUEST_ID = 'UNIQUE_PRICING_REQUEST_ID';
  const pricingVehicleV4Endpoint = 'Pricing/v4/VehiclePricing';
  const responseIndividual = await processRequest<
    ServerResponse<PricingV4Quotation>
  >({
    method: 'POST',
    url: pricingVehicleV4Endpoint,
    data: payload,
    uniqueIdentifier: UNIQUE_PRICING_REQUEST_ID,
    customErrorMessage: makeQuotationCreationError,
  });

  const individualQuotation = makeQuotationFromPricingV4(
    responseIndividual.data,
  );
  individualQuotation.productId = payload.productId;
  individualQuotation.quotationProfile = currentProfile;
  return individualQuotation;
};

export function keepOnlyAlphanumeric(input: string): string {
  return input.replace(/[^a-zA-Z0-9]/g, '');
}

export function sanitizeCarDetails(
  data: CarItemFromLicense,
): CarItemFromLicense {
  return {
    data: {
      ...data.data,
      engineSerial: keepOnlyAlphanumeric(data.data.engineSerial),
      vin: keepOnlyAlphanumeric(data.data.vin),
      chassis: keepOnlyAlphanumeric(data.data.chassis),
    },
  };
}
